import { computed, type ComputedRef } from 'vue';
import type { TooltipOptions } from '../Tooltip';
import { FieldType } from './Field.model';
import {
	formatFieldDate,
	formatFieldNumber,
	formatLongDate,
	formatTimeAgo,
	resolveStatusColor,
	toFieldString,
	type FieldFormatter,
	type FieldValue,
} from './Field.utils';

export type FieldDisplayProps = {
	value: FieldValue;
	type: FieldType;
	format: FieldFormatter;
	empty: string;
	trueLabel: string;
	falseLabel: string;
	tooltip?: string | TooltipOptions | null;
	maxChars?: number | null;
};

export type FieldDisplayState = {
	stringValue: ComputedRef<string>;
	displayValue: ComputedRef<string>;
	formattedDate: ComputedRef<string>;
	timeAgo: ComputedRef<string>;
	prettyDate: ComputedRef<string>;
	formattedNumber: ComputedRef<string>;
	booleanLabel: ComputedRef<string>;
	statusLabel: ComputedRef<string>;
	statusColor: ComputedRef<ReturnType<typeof resolveStatusColor>>;
	tooltipBindings: ComputedRef<TooltipOptions>;
	hasTooltip: ComputedRef<boolean>;
};

export const useFieldDisplay = (
	props: FieldDisplayProps
): FieldDisplayState => {
	const stringValue = computed(() => toFieldString(props.value));
	const shouldTruncate = computed(() => {
		if (props.type !== FieldType.TEXT) return false;
		if (!Number.isFinite(props.maxChars) || Number(props.maxChars) <= 0) {
			return false;
		}
		return stringValue.value.length > Number(props.maxChars);
	});
	const displayValue = computed(() => {
		if (!shouldTruncate.value) return stringValue.value;
		return `${stringValue.value.slice(0, Number(props.maxChars))}...`;
	});
	const formattedDate = computed(() =>
		formatFieldDate(props.value, props.format, props.empty, stringValue.value)
	);
	const timeAgo = computed(() =>
		formatTimeAgo(props.value, props.empty, stringValue.value)
	);
	const prettyDate = computed(() =>
		formatLongDate(props.value, props.empty, stringValue.value)
	);
	const formattedNumber = computed(() =>
		formatFieldNumber(props.value, props.empty)
	);
	const booleanLabel = computed(() =>
		props.value ? props.trueLabel : props.falseLabel
	);
	const statusLabel = computed(() =>
		stringValue.value ? stringValue.value : props.empty
	);
	const statusColor = computed(() => resolveStatusColor(stringValue.value));
	const truncationTooltipText = computed(() =>
		shouldTruncate.value ? stringValue.value : ''
	);

	const tooltipBindings = computed<TooltipOptions>(() => {
		if (typeof props.tooltip === 'string') {
			return {
				text: props.tooltip.trim().length
					? props.tooltip
					: truncationTooltipText.value,
			};
		}
		const tooltip = (props.tooltip || {}) as TooltipOptions;
		if (
			tooltip.text ||
			tooltip.html ||
			tooltip.actions?.length ||
			!truncationTooltipText.value
		) {
			return tooltip;
		}
		return {
			...tooltip,
			text: truncationTooltipText.value,
		};
	});

	const hasTooltip = computed(() => {
		if (typeof props.tooltip === 'string') {
			return (
				props.tooltip.trim().length > 0 || Boolean(truncationTooltipText.value)
			);
		}
		if (!props.tooltip) return Boolean(truncationTooltipText.value);
		const tooltip = props.tooltip as TooltipOptions;
		return Boolean(
			tooltip.text ||
			tooltip.html ||
			tooltip.actions?.length ||
			truncationTooltipText.value
		);
	});

	return {
		stringValue,
		displayValue,
		formattedDate,
		timeAgo,
		prettyDate,
		formattedNumber,
		booleanLabel,
		statusLabel,
		statusColor,
		tooltipBindings,
		hasTooltip,
	};
};
